import { Transition } from '@headlessui/react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import clsx from 'clsx';
import React from 'react';
import { StyledButton, getButtonClass } from './Button';
import Button from './Button';

const AlertDialog = ({
  trigger,
  title,
  options,
  isOpen,
  onProceed,
  onCancel,
  onDismiss,
  customContent,
}) => {
  // confirmDisabled renders the confirm action greyed-out and inert — used by
  // blocking dialogs that explain why the action can't proceed (the user can
  // only Cancel).
  const { description, cancel = 'Cancel', confirm = 'Confirm', confirmDisabled = false } = options;

  return (
    <AlertDialogPrimitive.Root open={isOpen}>
      <AlertDialogPrimitive.Trigger asChild>{trigger}</AlertDialogPrimitive.Trigger>

      <AlertDialogPrimitive.Portal>
        <Transition show={isOpen}>
          <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Overlay */}
            <Transition>
              <AlertDialogPrimitive.Overlay
                className={clsx(
                  // Position and background
                  'fixed inset-0 bg-black/50',
                  // Transition classes
                  'transition-opacity duration-300 ease-in-out',
                  'data-[closed]:opacity-0',
                )}
              />
            </Transition>

            {/* Dialog Content */}
            <Transition>
              <AlertDialogPrimitive.Content
                onEscapeKeyDown={onDismiss}
                className={clsx(
                  'fixed z-50',
                  'w-[95vw] max-w-lg rounded-lg p-6 md:w-full',
                  'top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]',
                  'bg-white dark:bg-background dark:border dark:border-border',
                  'focus:outline-none focus-visible:ring-3 focus-visible:ring-ring',
                  // Transition styles
                  'transition ease-in-out duration-300',
                  'data-[closed]:opacity-0 data-[closed]:scale-95',
                  'data-[enter]:duration-200',
                  'data-[leave]:duration-150',
                )}
              >
                <AlertDialogPrimitive.Title className="text-lg font-bold text-gray-900 dark:text-foreground">
                  {title}
                </AlertDialogPrimitive.Title>
                <AlertDialogPrimitive.Description className="mt-2 text-sm text-gray-700 dark:text-muted-foreground">
                  {description}
                </AlertDialogPrimitive.Description>
                {customContent && <div className="mt-4">{customContent}</div>}
                <div className="mt-6 flex justify-end space-x-4">
                  <AlertDialogPrimitive.Cancel asChild>
                    <Button onClick={onCancel} type="button" appearance="secondary">
                      {cancel}
                    </Button>
                  </AlertDialogPrimitive.Cancel>
                  <AlertDialogPrimitive.Action asChild>
                    <StyledButton
                      className={clsx(
                        'no-underline transition-colors font-medium focus:outline-none border border-transparent py-2 px-2',
                        getButtonClass('primary'),
                      )}
                      onClick={onProceed}
                      type="button"
                      disabled={confirmDisabled}
                    >
                      {confirm}
                    </StyledButton>
                  </AlertDialogPrimitive.Action>
                </div>
              </AlertDialogPrimitive.Content>
            </Transition>
          </div>
        </Transition>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
};

export default AlertDialog;
